import { useGetMentorQualityScores } from '@actions';
import { ReactComponent as SettingsIcon } from '@assets/icons/V4/settings-icon.svg';
import AgoraSpinner from '@components/V3/Utils/AgoraSpinner';
import IconButton from '@components/V4/IconButton';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import {
  DIMENSION_SHORT_LABELS,
  formatScore,
  scoreTone,
} from '@components/V4/QualityScore/QualityScoreDetails';
import Table from '@components/V4/Table/Table';
import { SessionQualityScoreDimensionKey } from '@shared/common';
import useClientPagination from 'apps/agora/src/hooks/useClientPagination';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useContext } from 'react';
import SessionModalEmptyState from '../../../DetailsModalEmptyState';
import SessionModalSection from '../../../DetailsModalSection';
import { MentorDetailsPageContext } from '../../MentorDetailsPage';

const StatCard = (props: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-2 rounded-2xl bg-surfaceObject p-4">
    <span className="text-xs text-customGrey">{props.label}</span>
    <div className="text-2xl font-bold leading-none tabular-nums">{props.children}</div>
  </div>
);

const QualityScoreTab = () => {
  const { mentor, onSessionModalOpen } = useContext(MentorDetailsPageContext);
  const { data, isLoading } = useGetMentorQualityScores(
    { mentorId: mentor?._id },
    { enabled: !!mentor?._id }
  );
  const summary = data?.[0];

  const [paginatedSessions, pagination] = useClientPagination({
    data: summary?.sessions ?? [],
    shouldOnlyShowPages: true,
    initialPageSize: 10,
  });

  if (isLoading) return <AgoraSpinner />;

  if (!summary) return <SessionModalEmptyState />;

  return (
    <div className="mt-6 flex flex-col gap-6">
      <section className="grid grid-cols-2 gap-3 laptop:grid-cols-5">
        <StatCard label="Average AI score">
          <QualityScoreBadge score={summary.average} size="large" />
        </StatCard>
        <StatCard label="Graded sessions">
          <span className="text-white">{summary.graded}</span>
        </StatCard>
        <StatCard label="Good (≥ 4)">
          <span className="text-customGreen">{summary.good}</span>
        </StatCard>
        <StatCard label="OK (3 – 4)">
          <span className="text-customGold">{summary.ok}</span>
        </StatCard>
        <StatCard label="Poor (< 3)">
          <span className="text-customRed">{summary.poor}</span>
        </StatCard>
      </section>

      {summary.dimensions.length > 0 && (
        <SessionModalSection sectionTitle="Average by dimension">
          <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3 laptop:grid-cols-6">
            {summary.dimensions.map((dimension) => (
              <div
                key={dimension.key}
                className="flex flex-col gap-1 rounded-2xl bg-surfaceBackground p-4"
              >
                <span className="text-xs text-customGrey">
                  {dimension.key}.{' '}
                  {dimension.name ??
                    DIMENSION_SHORT_LABELS[dimension.key as SessionQualityScoreDimensionKey] ??
                    ''}
                </span>
                <span
                  className={mergeClassNames(
                    'text-2xl font-bold leading-none tabular-nums',
                    scoreTone(dimension.average)
                  )}
                >
                  {formatScore(dimension.average)}
                </span>
                <span className="text-xs text-customGrey">
                  {dimension.sampleSize} {dimension.sampleSize === 1 ? 'session' : 'sessions'}
                </span>
              </div>
            ))}
          </div>
        </SessionModalSection>
      )}

      <SessionModalSection sectionTitle="Graded sessions">
        <Table size="small" pagination={pagination}>
          <Table.Header>
            <Table.Row isHeader>
              <Table.HeaderCell>Meeting date</Table.HeaderCell>
              <Table.HeaderCell>Student</Table.HeaderCell>
              <Table.HeaderCell>Course</Table.HeaderCell>
              <Table.HeaderCell>Session</Table.HeaderCell>
              <Table.HeaderCell>AI Score</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedSessions?.map((session) => (
              <Table.Row key={session.sessionId}>
                <Table.Cell>
                  {session.meetingDate ? moment(session.meetingDate).format('DD.MM.YYYY') : 'n/a'}
                </Table.Cell>
                <Table.Cell>{session.studentName ?? 'n/a'}</Table.Cell>
                <Table.Cell>{session.courseTitle ?? 'n/a'}</Table.Cell>
                <Table.Cell onClick={() => onSessionModalOpen(session.sessionId)}>
                  {session.sessionName ?? 'n/a'}
                </Table.Cell>
                <Table.Cell>
                  <QualityScoreBadge score={session.score} />
                </Table.Cell>
                <Table.Cell>
                  <IconButton
                    size="small"
                    variant="ghost"
                    icon={<SettingsIcon />}
                    onClick={() => onSessionModalOpen(session.sessionId)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </SessionModalSection>
    </div>
  );
};

export default QualityScoreTab;
